import React from 'react'
import classnames from 'classnames'
import './Typography.scss'

type TagTypes = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'span'

export type FontSizes =
  | '2xs'
  | 'xs'
  | 'sm'
  | 'normal'
  | 'lg'
  | 'xl'
  | '2xl'
  | '3xl'
  | '4xl'

type FontWeights = 'bold' | 'normal' | 'bolder'

export type FontColors =
  | 'black'
  | 'grey'
  | 'darkGrey'
  | 'white'
  | 'blue'
  | 'green'
  | 'orange'
  | 'red'
  | 'blackPearl'
  | 'dimGrey'

type FontFamily = 'Roboto' | 'Gibson'

type TypographyProps = {
  text?: string
  tag?: TagTypes
  size?: FontSizes
  weight?: FontWeights
  color?: FontColors
  italic?: boolean
  underline?: boolean
  uppercase?: boolean
  family?: FontFamily
  muted?: boolean
  className?: string
  children?: React.ReactNode
}

/**
 * A responsive, customizable component for rendering basic HTML text elements.
 * You may render children, or use the 'text' prop for short, one liners.
 ** You'll notice that `h1` through `h6` will always be `bold`. This is intentional per the design.
 ** Any semantic text tag can be given any text size set.
 ** Ex: an `h1` tag can be given a `size` of `2xs`, or a `<p>` tag can be given size `3xl`
 ** Ex: an `h1` tag can be given a `size` of `2xs`, or a `<p>` tag can be given size `3xl`
 ** `span` and `p` tags can be given weight of `normal` or `bold` (`normal` is default).
 ** Font size for a given size 'set' will responsively update between mobile and md viewports.
 * @param tag The html text element you want to render.
 * @param size The desired responsive font-size set.
 * @param weight normal or bold, your desired font weight.
 * @param color The color of the text you wish to render.
 * @param family The font family of the text. Roboto or Gibson
 * @param muted a boolean to convert the text to muted (50% lighter)
 * @param uppercase a boolean to convert your text to uppercase.
 * @param text use this prop if you prefer to use <Typography text="text here" /> syntaax.
 * @param className any extra classnames you wish to pass to style this element.
 * @author Brendan Roman
 */
export const Typography: React.FC<TypographyProps> = ({
  text,
  tag: TypographyTag = 'span',
  uppercase = false,
  size = 'normal',
  weight = 'normal',
  color = 'black',
  italic = false,
  underline = false,
  family = 'Roboto',
  muted = false,
  className = '',
  children,
}) => {
  const typographyClass = classnames(
    {
      Typography: true,
      'Typography--style--italic': italic,
      'Typography--decoration--underline': underline,
      'Typography--uppercase': uppercase,
    },
    `Typography--${TypographyTag} Typography--sizeSet--${size} Typography--weight--${weight} Typography--family--${family}`,
    muted ? `Typography--${color}--muted` : `Typography--${color}`
  )

  return (
    <TypographyTag className={`${typographyClass} ${className}`}>
      {text ? text : children}
    </TypographyTag>
  )
}
